import React, { useCallback, useEffect, useMemo } from 'react';
// import { useQuery } from 'react-query';
import { Form, Popconfirm, Input, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import DF from 'DisplayFramework';
import { indexBy } from 'utils/utils2';
import SaveButton from 'components/SaveButtonAction';
import DataGrid from 'components/DataGrid/DataGrid';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { useFormList } from 'hooks/useFormList';
import {
  addOfferPricingAttributeCriteria,
  getOfferPricingAttributeCriteriaList,
  // // getPricingCommissionCriteria,
  // addPricingCommissionCriteria,
  // removePricingCommissionCriteria,
  ParPricingCommCriteriaResponse,
  removeOfferPricingAttributeCriteria,
} from 'services/pricing';
import DeleteButton from 'components/DeleteButton';
import { useQuery } from 'react-query';
import ButtonOperation from 'components/ButtonOperation';
import ApplicationRule from 'pages/Configuration/RuleEngine/ApplicationRule';

const NEW_PRICING_CRITERIA_ID = -1;

type OfferPricingApplicationsAttributeProps = {
  attid: number;
};

const OfferPricingApplicationsAttribute = (
  props: OfferPricingApplicationsAttributeProps
) => {
  const [form] = Form.useForm();
  const fieldName = 'criteria';

  const { attid } = props;

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'criid',
    addAsFirst: true,
  });

  const { data, refetch, isFetching } = useQuery(
    ['getOfferPricingAttributeCriteriaList', { attid }],
    () =>
      getOfferPricingAttributeCriteriaList({ attid }).then(
        (res) => res.list || []
      ),
    {
      enabled: attid !== 0,
    }
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data,
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [data]);

  const addRow = () => {
    const row: any = {
      attid: attid,
      criid: NEW_PRICING_CRITERIA_ID,
      critcode: '',
      critcodelabel: '',
      critvalue: '',
      critvaluelabel: '',
      critvaluemax: '',
      critoperand: '',
      listvalue: '',
      applicationrule: '',
    };
    add(row);
  };

  const onSave = useCallback(
    (record: any) => {
      const steps: any[] = form.getFieldValue(fieldName);
      const index = indexBy(steps, 'criid')[record.criid];
      const currentValue = steps[index];
      addOfferPricingAttributeCriteria({
        req: currentValue,
      }).then((response) => {
        if (response.code === '200') {
          notification.success({
            message: response.message,
          });
          refetch();
        }
        refetch();
      });
    },
    [form, refetch]
  );

  const columns = useMemo<DataGridColDef<ParPricingCommCriteriaResponse>[]>(
    () => [
      {
        dataIndex: 'criid',
        width: 100,
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
              'criid',
            ]}
          >
            {_value}
          </Form.Item>
        ),
      },
      {
        dataIndex: 'critcode',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
              'critcode',
            ]}
          >
            <ParListSelect parcode="PRCRITERIA" />
          </Form.Item>
        ),
        width: 180,
      },
      {
        dataIndex: 'critoperand',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
              'critoperand',
            ]}
          >
            <ParListSelect parcode="CROPERAND" />
          </Form.Item>
        ),
        width: 180,
      },
      {
        dataIndex: 'critvalue',
        render: (_value, _record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name={[
                  fieldName,
                  indexBy(getFieldValue(fieldName), 'criid')[_record.criid],
                  'critvalue',
                ]}
                rules={[{ required: true }]}
              >
                {getFieldValue([fieldName, index, 'critoperand']) ===
                'VALUE' ? (
                  <ParListSelect
                    parcode={getFieldValue([fieldName, index, 'critcode'])}
                  />
                ) : getFieldValue([fieldName, index, 'critoperand']) ===
                  'LIST' ? null : (
                  <Input />
                )}
              </Form.Item>
            )}
          </Form.Item>
        ),
        width: 160,
      },
      {
        dataIndex: 'critvaluemax',
        render: (_value, _record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name={[
                  fieldName,
                  indexBy(getFieldValue(fieldName), 'criid')[_record.criid],
                  'critvaluemax',
                ]}
                rules={[{ required: true }]}
              >
                {getFieldValue([fieldName, index, 'critoperand']) ===
                'BETWEEN' ? (
                  <Input />
                ) : null}
              </Form.Item>
            )}
          </Form.Item>
        ),
        width: 160,
      },
      {
        dataIndex: 'listvalue',
        render: (_value, _record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name={[
                  fieldName,
                  indexBy(getFieldValue(fieldName), 'criid')[_record.criid],
                  'listvalue',
                ]}
                rules={[{ required: true }]}
              >
                {getFieldValue([fieldName, index, 'critoperand']) === 'LIST' ? (
                  <Input />
                ) : null}
              </Form.Item>
            )}
          </Form.Item>
        ),
        width: 160,
      },
      {
        dataIndex: 'applicationrule',
        render: (_value, _record, index) => (
          <div>
            <ApplicationRule
              noStyle
              form={form}
              module="PROPOSAL"
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
                'applicationrule',
              ]}
              disabled={_record.critcode !== 'RULE'}
            />
          </div>
        ),
        width: 160,
      },
      {
        key: 'actions',
        render: (_, _record) => (
          <>
            {_record.criid === NEW_PRICING_CRITERIA_ID && (
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                }}
                size="small"
                comcode="save"
              />
            )}
            {_record.criid === NEW_PRICING_CRITERIA_ID ? (
              <DeleteButton onlyIcon onClick={() => remove(_record.criid)} />
            ) : (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  removeOfferPricingAttributeCriteria({
                    criid: _record.criid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            )}
          </>
        ),
        width: 100,
        fixed: 'right',
      },
    ],
    [refetch, form, data]
  );

  return (
    <DF.Provider pagcode="offerpricingcriterialist">
      <Form form={form} initialValues={INITIAL_VALUES}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const criteria: ParPricingCommCriteriaResponse[] =
              getFieldValue(fieldName);

            return (
              <DataGrid
                rowKey="criid"
                columns={columns}
                dataSource={criteria}
                hideFilters
                isLoading={isFetching}
                titleComcode="offerPricingattributeCriteria"
                extraActions={
                  <ButtonOperation
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addRow}
                    comcode="add"
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

const INITIAL_VALUES = {
  criteria: [],
};

export default OfferPricingApplicationsAttribute;
